import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView, trackVehicleView } from '../utils/analytics';
import { buildStructuredData, getPageMetadata } from '../seo/siteMetadata.browser';


function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
}

function upsertCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function updateStructuredData(schemas) {
  document.head.querySelectorAll('script[data-deroyalty-seo="structured-data"]')
    .forEach((element) => element.remove());

  schemas.forEach((schema, index) => {
    const element = document.createElement('script');
    element.type = 'application/ld+json';
    element.dataset.deroyaltySeo = 'structured-data';
    element.id = `deroyalty-structured-data-${index + 1}`;
    element.textContent = JSON.stringify(schema).replace(/</g, '\\u003c');
    document.head.appendChild(element);
  });
}

function SeoManager({ products = [], categories = [] }) {
  const location = useLocation();
  const viewedVehicleId = useRef(null);
  const page = useMemo(
    () => getPageMetadata(location.pathname, products, categories),
    [categories, location.pathname, products],
  );
  const schemas = useMemo(
    () => buildStructuredData(page, products),
    [page, products],
  );

  useEffect(() => {
    document.title = page.title;
    document.documentElement.lang = 'en-ZW';

    upsertMeta('meta[name="description"]', { name: 'description', content: page.description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: page.robots });
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot', content: page.robots });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'DeRoyalty Car Rental' });
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_ZW' });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: page.type });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: page.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: page.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: page.canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: page.image });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: page.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: page.description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: page.image });
    upsertCanonical(page.canonicalUrl);
    updateStructuredData(schemas);
  }, [page, schemas]);

  useEffect(() => {
    const pageLocation = `${window.location.origin}${location.pathname}${location.search}`;
    trackPageView({ location: pageLocation, title: page.title });
  }, [location.pathname, location.search, page.title]);

  useEffect(() => {
    const productId = page.product?.id;
    if (!productId || viewedVehicleId.current === productId) return;
    viewedVehicleId.current = productId;
    trackVehicleView(page.product);
  }, [page.product]);

  return null;
}

export default SeoManager;
